import { action, computed, observable, toJS } from "mobx";
import api from "../api";
import http from "../api/client";
import { ExtendedAuthPasswordData } from "../api/profile";
import { IPhone, IStudentPirate } from "../entryes/panel/Profile/types";
import { ICountryCode } from "../typings/ICountryCode";
import { ICurrency } from "../typings/ICurrency";
import { ILocalization } from "../typings/ILocalization";
import { StudentStatus } from "../typings/IStudent";
import { IStudentProfile } from "../typings/IStudentProfile";
import { emailValidation, tl } from "../utils";
import StudentNotesStore from "./studentNotes.store";


const extendedAuthErrorDict : {[key: string] : string} = {
  'Wrong password repeat': tl('Пароли должны совпадать'),
  'Password used in last 3 times': tl('Этот пароль уже был использован'),
  'Bad new password': tl('Некорректный новый пароль'),
  'Wrong current password': tl('Неверный текущий пароль'),
  'ServerError': tl('Ошибка сервера, повторите позже')
}


export default class ProfileStore {
  @observable
  rootStore: any;
  @observable
  showSecure: boolean;
  @observable
  showNotifications: boolean;
  @observable
  profile: IStudentProfile;
  @observable
  profileModel: IStudentProfile

  @observable
  loadingDoubleAuth: boolean;
  @observable
  secretKey: string
  @observable
  qrCode: string

  @observable
  extendedAuthError: string;

  @observable
  passwordChangeError: string;

  @observable 
  notes: StudentNotesStore;

  @observable error: any;
  @observable isLoading: boolean;

  @observable
  removeTagsModal: boolean;

  @observable currentModule: "student" | "panel";

  @observable dialog: {
    type: string | null;
    studentId: number;
    groupId: number;
    courseId: number;
    noteId: number;
    status: StudentStatus;
  };

  @observable currentDictionary: number;
  @observable languages: ILocalization[];
  @observable countryCodes: ICountryCode[];
  @observable currencies: ICurrency[];

  constructor(RootStore: any) {
    this.rootStore = RootStore;
    // this.notes = new StudentNotesStore(this.rootStore, this.profile);
    this.error = false;
    this.isLoading = true;
    this.showNotifications = false;
    this.showSecure = false;
    this.removeTagsModal = false;
    this.dialog = {
      type: null,
      studentId: -1,
      groupId: -1,
      courseId: -1,
      noteId: -1,
      status: "active",
    };
  }

  @action
  async init(id: number | string) {
    this.isLoading = true
    const response = await api.profile.byId(id)
    if (!this.currentDictionary) {
      await this.getDictionaries(()=>{});
    }
    this.notes = new StudentNotesStore(this.rootStore, response);
    this.showNotifications= response.showNotifications;
    this.showSecure= response.showSecure;
    this.profile = response
    this.profileModel = response;
    this.isLoading = false;
  }

  @computed
  get fullName() {
    if (this.profile.user.name || this.profile.user.surname) {
      return `${this.profile.user.name || ''} ${this.profile.user.surname || ''}`
    } else {
      return this.profile.user.email;
    }
  }

  @action
  setStudent (data: any) {
    this.profile = { ...this.profile, ...data };
  };

  @action
  setInitialStore (data: any) {
    this.isLoading = false;
    this.profile = data.student;
    this.currentModule = data.module;
  };

  @computed get isError() {
    return this.error;
  }
  @action
  setError (value: boolean = true) {
    this.error = value;
  };

  @action
  setLoading (value: boolean = true) {
    this.isLoading = value;
  };

  @action
  setDialog = (
    type: string | null,
    groupId?: number,
    courseId?: number,
    noteId?: number,
    status?: StudentStatus
  ) => {
    this.dialog.type = type;
    if (courseId) {
      this.dialog.courseId = courseId;
    }
    if (noteId) {
      this.dialog.noteId = noteId;
    }
    if (groupId) {
      this.dialog.groupId = groupId;
    }
    if (status) this.dialog.status = status;
  };

  @action
  async updateProfile (student: IStudentProfile, callback?: any) {
    const emailValid = emailValidation(student.user.email);
    if (!emailValid && !this.hasConfirmedPhoneNumber) {
      this.rootStore.fsNotifierStore.addNotice(
        tl(`У профиля нет подтвержденного email или подтвержденного номера телефона`),
        "error"
      );
      return;
    }
    if (!student.user.name) {
      this.rootStore.fsNotifierStore.addNotice(
        tl(`Профиль не был сохранен. Заполните все обязательные поля.`),
        "error"
      );
      return;
    }
    try {
      await api.profile.update(student);
      this.rootStore.fsNotifierStore.addNotice(
        tl(`Профиль успешно обновлен.`),
        "success"
      );
      this.profile = student;
      if (callback) callback();
    } catch (error) {
      let errorMessage = tl(`Произошла ошибка. Профиль не был обновлен.`);
      if(error.response?.data?.errorMessage) {
        errorMessage += '\r\n' + tl(error.response.data.errorMessage);
      }
      this.rootStore.fsNotifierStore.addNotice(
        errorMessage,
        "error"
      );
    }
  };

  @action
  async deleteAccount() {
    try {
      const response = await api.profile.remove(this.profile.id)
      if (response.redirectUrl) {
        location.href = response.redirectUrl;
      } else {
        location.href = "/login";
      }
    } catch (_) {
      this.rootStore.fsNotifierStore.addNotice(
        tl(`Произошла ошибка. Не удалось удалить аккаунт.`),
        "error"
      );
    }
  };
  @action
  async validatePhone (phone: string) {
    const res = api.profile.validatePhone(phone);
    if(!res) {
      return false;
    } else {
      return true;
    }
  }

  @action
  async confirmPhone (number: string, code?: string, callback?: any) {
    try {
      if (!code) {
          await api.profile.checkPhone(number, this.profile.user.email);
          return;
      }
      const response = await api.profile.confirmPhone({phone: number, code: code, email: this.profile.user.email});
      if (response.status === 'success') {
        callback(true);
        this.rootStore.fsNotifierStore.addNotice(
          tl(`Телефон успешно подтвержден.`),
          "success"
        );
      } else {
        callback(false);
        this.rootStore.fsNotifierStore.addNotice(
          tl(`Не удалось подтвердить ваш номер телефона.`),
          "error"
        );
      }
    } catch (error) {
      if (error?.response.status === 429) {
        this.rootStore.fsNotifierStore.addNotice(
          tl(`Исчепарн лимит запросов на изменение телефона`),
          "error"
        );
        callback(false)
        return;
      }
      if (error?.response?.data?.message === "Confirm error") {
        this.rootStore.fsNotifierStore.addNotice(
          tl(`Не удалось подтвердить ваш номер телефона.`),
          "error"
        );
      } else {
        this.rootStore.fsNotifierStore.addNotice(
          tl(
            `Произошла ошибка. Не удалось отправить код на ваш телефонный номер. Обратитесь в поддержку.`
          ),
          "error"
        );
      }
      callback(false);
    }
  };


  @action
  async change2fa(type: T2FA) {
    this.loadingDoubleAuth = true;
    try {
      const response = await api.profile.change2fa(type);
      if (response.type === 'google') {
        this.qrCode = response.qrCode
        this.secretKey = response.secretKey
      }
      if (type !== 'google') {
        this.profile.security.doubleAuth.type = type;
      }
      this.loadingDoubleAuth = false;
      return response;
    } catch (error) {
      this.rootStore.fsNotifierStore.addNotice(
        tl(`Попробуйте позже`),
        "info"
      );
      this.loadingDoubleAuth = false;
    }
  }

  @action 
  async changePassword({password, new_password, extended_auth = false}: ExtendedAuthPasswordData) {
    try {
      await api.profile.changePassowrd({password, new_password, new_password_repeat: new_password, extended_auth});
      this.profile.security.extendedAuth = extended_auth;
      this.extendedAuthError =  '';
      this.passwordChangeError = '';
      this.rootStore.fsNotifierStore.addNotice(
        tl(`Пароль успешно изменен`),
        "success"
      );
      this.profile.security.lastPasswordChangeTime = new Date().toISOString()
    } catch (error) {
      const message = error?.response?.data?.errorMessage
      if (error.response?.status === 500) {
        this.rootStore.fsNotifierStore.addNotice(
          tl(`Ошибка сервера, повторите позже`),
          "info"
        );
        throw new Error('server error');
      }
      if (message) {
        if (extended_auth) {
          this.extendedAuthError = extendedAuthErrorDict[message];
          throw new Error('invalid');

        } 
        this.passwordChangeError = extendedAuthErrorDict[message];
        throw new Error('invalid');

      } 
      throw new Error('error');
    }
  }
  @computed
  get hasConfirmedPhoneNumber() {
    return this.profile.phones.some((phone) => phone.isMain && phone.isConfirmed);
  }
  @action
  async verifyGoogleAuth(verifyCode?: string) {
    this.loadingDoubleAuth = true;
    try {
      const response = await api.profile.verifyGoogleAuth({
        verifyCode: verifyCode,
        secretKey: this.secretKey,
      });
      this.profile.security.doubleAuth.type = 'google';
      this.loadingDoubleAuth = false;

    } catch (error) {
      console.log(error, error.response)
      this.loadingDoubleAuth = false;
      
    }
  }
  @action
  async updateNotifications() {
    try {
      await api.profile.updateNotificationSettings(this.profileModel.notifications);
      this.profile.notifications = {...toJS(this.profileModel.notifications)}
      this.rootStore.fsNotifierStore.addNotice(
        tl("Настройки уведомлений обновлены"),
        "info"
      );
    } catch (error) {
      console.log(error);
    }
  }

  //Dictionary
  @computed 
  get getCurrencyName() {
    return (id: string) => {
      const currency = this.currencies.find((a) => a.code === id);
      return currency?.name;
    };
  }
  @action 
  async getDictionaries (callback?: () => void, id?: number) {
    const response = await http.get(`/api/XHR/get`,
        {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          params: {
            language: 'list',
            currency: 'list',
            paymentsystems: 'list',
            phones: 'list',
            group_id: id
          }
        }
      )
    this.currencies = response.data.currency.list;
    this.languages = response.data.language.list;
    this.currentDictionary = 1;
    this.countryCodes = response.data.phones.list;
    callback && callback();
  };
  @action
  setPirate = (data: IStudentPirate) => {
    this.profile.pirate = data;
  };
}

export { ProfileStore };
